import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Upload, Loader2, X, GripVertical, ChevronUp, ChevronDown, ClipboardPaste } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export interface ImageItem {
  url: string;
  order: number;
}

interface MultiImageUploadProps {
  images: ImageItem[];
  label: string;
  tempId: string;
  uploading: boolean;
  onImageUpload: (file: File) => Promise<void>;
  onImagesChange: (images: ImageItem[]) => void;
  onPaste?: () => void;
  onFocus?: () => void;
}

export const MultiImageUpload = ({
  images,
  label,
  tempId,
  uploading,
  onImageUpload,
  onImagesChange,
  onPaste,
  onFocus,
}: MultiImageUploadProps) => {
  const { t } = useTranslation();
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const sortedImages = [...(images || [])].sort((a, b) => a.order - b.order);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        await onImageUpload(file);
      } else {
        toast.error(t('errors.notImage'));
      }
    }
  };

  const moveImage = (fromIndex: number, toIndex: number) => {
    const newImages = [...sortedImages];
    const [moved] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, moved);
    
    // Renumber orders
    const reordered = newImages.map((img, idx) => ({
      ...img,
      order: idx,
    }));
    onImagesChange(reordered);
  };

  const removeImage = (index: number) => {
    const newImages = sortedImages.filter((_, idx) => idx !== index);
    // Renumber orders
    const reordered = newImages.map((img, idx) => ({
      ...img,
      order: idx,
    }));
    onImagesChange(reordered);
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div 
        className={cn(
          "border-2 border-dashed rounded-lg p-4 transition-colors",
          isDraggingOver ? "border-primary bg-primary/5" : "border-border",
          uploading && "opacity-50"
        )}
        tabIndex={0}
        onClick={onFocus}
        onFocus={onFocus}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept="image/*"
          id={`image-${tempId}`}
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onImageUpload(file);
          }}
        />
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onFocus?.();
              document.getElementById(`image-${tempId}`)?.click();
            }}
            disabled={uploading}
            className="w-full"
          >
            {uploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('editor.uploading')}
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                {t('editor.uploadImage')}
              </>
            )}
          </Button>
          {onPaste && (
            <Button
              type="button"
              variant="secondary"
              onClick={onPaste}
              className="w-full"
              disabled={uploading}
            >
              <ClipboardPaste className="mr-2 h-4 w-4" />
              {t('editor.pasteFromClipboard')}
            </Button>
          )}
        </div>
        {isDraggingOver && (
          <p className="text-center text-sm text-muted-foreground mt-2">
            {t('multiImageUpload.dropHere')}
          </p>
        )}
      </div>

      {sortedImages.length > 0 && (
        <div className="space-y-2 mt-3">
          {sortedImages.map((image, index) => (
            <div
              key={index}
              className="relative rounded-lg overflow-hidden border border-border group flex items-center gap-2 p-2"
            >
              <div className="flex flex-col gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => moveImage(index, Math.max(0, index - 1))}
                  disabled={index === 0}
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <GripVertical className="h-4 w-4 text-muted-foreground" />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => moveImage(index, Math.min(sortedImages.length - 1, index + 1))}
                  disabled={index === sortedImages.length - 1}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>
              <img
                src={image.url}
                alt={t('multiImageUpload.imageAlt', { index: index + 1 })}
                className="h-20 w-20 object-cover rounded"
              />
              <div className="flex-1 text-sm text-muted-foreground">
                {t('multiImageUpload.imageLabel', { index: index + 1 })}
              </div>
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="h-8 w-8"
                onClick={() => removeImage(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
